<template>
  <div class="flex flex-wrap gap-x-4 mb-36" v-if="User.info.client">
    <div class="bg-white w-[99%] m-auto md:ml-2 md:mx-0 lg:w-3/5 shadow-ts1 rounded-lg">
      <div class="border-b border-gray-300 w-full px-4 md:px-8 pt-5 pb-4">
        <h2 class="font-display text-xl font-bold text-blue leading-none">{{ $t('profile').tTitle() }}</h2>
      </div>

      <!-- Profile Avatar and Details -->
      <div class="border-b border-gray-300 mx-4 md:mx-8 py-8">
        <div class="md:flex items-center">
          <!-- Avatar -->
          <div class="flex h-[180px] w-[180px] m-auto mb-8 md:mb-0 md:m-0 text-6xl font-display font-bold justify-center items-center text-white bg-blue rounded-full shrink-0">
            <div>{{ initials }}</div>
          </div>
          <!-- User Info -->
          <div class="ltr:ml-7 rtl:mr-7">
            <div class="text-2xl md:text-3xl font-bold text-center ltr:md:text-left rtl:md:text-right text-gray-900 font-display">
              {{ displayName }}
            </div>
            <div class="mt-1.5 text-center ltr:md:text-left rtl:md:text-right text-base font-semibold" :class="accountStateClass">
              {{ $t('client_state').tTitle() }}: {{ accountStateText }}
            </div>
          </div>
        </div>
      </div>

      <!-- Individual or Company Fields -->
      <div class="p-4 md:p-8 pb-4">
        <profile-field v-for="field in profileFields" :key="field.title" :title="$t(field.title).tTitle()" :value="field.value" disabled />
      </div>

      <!-- Section Header -->
      <div class="border-b border-gray-300 mx-4 md:mx-8 pb-6">
        <h2 class="font-display text-xl font-bold text-blue">{{ $t('personal_details').tTitle() }}</h2>
      </div>

      <!-- Personal Details -->
      <div class="p-4 md:p-8 pb-4">
        <profile-field v-for="field in personalDetailsFields" :key="field.title" :title="$t(field.title).tTitle()" :value="field.value" disabled />
      </div>
    </div>

    <!-- Change Password Section -->
    <div class="w-full md:flex-1 self-start m-auto md:m-0 mb-20 lg:mb-0 mx-2 md:mx-0">
      <div class="bg-white m-auto mt-8 md:mr-2 lg:mt-0 shadow-ts1 rounded-lg">
        <change-password />
      </div>
    </div>

    <logo-loader v-if="logoLoaderActive && ongoingCalls > 0"></logo-loader>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ChangePassword from './ChangePassword.vue'
import ProfileField from '../../components/ProfileField.vue'
import config from '../../config'
import LogoLoader from '@/components/LogoLoader.vue'

export default {
  components: {
    ChangePassword,
    ProfileField,
    LogoLoader,
  },
  data() {
    return {
      options: {
        newsletter: false,
        vps: false,
      },
      countries: config.countries,
    }
  },
  computed: {
    ...mapGetters({
      User: 'user/all',
      UserInfo: 'user/info',
      Language: 'language/code',
      ongoingCalls: 'logoLoader/ongoingCalls',
      logoLoaderActive: 'logoLoader/active',
    }),

    // Derived User Information
    initials() {
      const { first_name, last_name, client } = this.UserInfo
      if (first_name && last_name) return (first_name[0] + last_name[0]).toUpperCase()
      if (client?.company_name) return client.company_name[0].toUpperCase()
      return ''
    },
    displayName() {
      const { first_name, last_name, client } = this.UserInfo
      if (first_name || last_name) return `${first_name} ${last_name}`.trim()
      if (client?.company_name) return client.company_name
      return ''
    },

    // Account State
    accountStateClass() {
      const state = this.UserInfo.client?.account_state
      return {
        'text-green': state === 'full',
        'text-orange-light': state === 'pending',
        'text-red': ['rejected', 'incomplete'].includes(state),
      }
    },
    accountStateText() {
      const state = this.UserInfo.client?.account_state
      if (state === 'full') return this.$t('client_state_approved').tTitle()
      if (state === 'pending') return this.$t('client_state_pending').tTitle()
      if (state === 'incomplete') return this.$t('client_state_incomplete').tTitle()
      return ''
    },

    // Profile Fields
    isIndividual() {
      return !this.UserInfo.client?.company_name
    },
    profileFields() {
      return this.isIndividual
        ? [
            { title: 'first_name', value: this.UserInfo.first_name },
            { title: 'middle_name', value: this.UserInfo.middle_name },
            { title: 'last_name', value: this.UserInfo.last_name },
          ]
        : [{ title: 'company_name', value: this.UserInfo.client?.company_name }]
    },

    // Personal Details Fields
    personalDetailsFields() {
      const { info, wallet } = this.User
      return [
        { title: 'email', value: info.email },
        { title: 'username', value: info.username },
        { title: 'website', value: info.client?.website },
        {
          title: this.isIndividual ? 'date_of_birth' : 'date_of_registration',
          value: info.date_of_birth != '0000-00-00' ? this.$formatDate(info.date_of_birth) : '',
        },
        {
          title: 'gender',
          value: this.isIndividual ? (info.gender ? (info.gender === '' ? this.$t('no_gender').tTitle() : info.gender.tTitle()) : this.$t('no_gender').tTitle()) : null,
        },
        { title: 'phone', value: info.phone },
        { title: 'country', value: this.countries[info.country_code]?.name },
        { title: 'address', value: info.address },
        { title: 'year_moved_to_address', value: info.year_moved_to_address },
        { title: 'post_code', value: info.post_code },
        { title: 'selected_currency', value: wallet.currency?.toUpperCase() || '' },
      ].filter(field => field.value !== null)
    },
  },
  mounted() {
    this['logoLoader/setManualLoader'](true)
    this.$nextTick(() => {
      setTimeout(() => {
        this['logoLoader/setManualLoader'](false)
      }, 1500)
    })
  },
  methods: {
    ...mapActions(['logoLoader/set', 'logoLoader/setManualLoader']),
  },
}
</script>
